import { TableBody, TableRow, TableCell, Link, Chip, Checkbox, Tooltip, IconButton, useTheme, Stack, Box, Typography, CircularProgress, Menu, MenuItem, Button } from '@mui/material';
import { PaginatedTableColumn, PaginatedTableColumnType } from '.';
import { formatCurrencyNumber, formatDate, formatDateTime, formatIntPhoneNumber } from '../../helpers/formatting-utils';
import LinearProgress from '@mui/material/LinearProgress';
import dayjs from 'dayjs';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useState, useRef, useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import chroma from 'chroma-js';
import cssColors from 'css-color-names';
import JsBarcode from 'jsbarcode';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

// Helper function to detect and format ISO date strings
const detectAndFormatDate = (value: any): React.ReactNode | null => {
  if (!value || typeof value !== 'string') return null;
  
  // Check if it's an ISO date string pattern (YYYY-MM-DDTHH:mm:ss.sssZ or similar)
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  
  if (isoDatePattern.test(value)) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const dateStr = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: '2-digit', 
          year: 'numeric' 
        });
        const timeStr = date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        });
        
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {dateStr}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {timeStr}
            </Typography>
          </Box>
        );
      }
    } catch {
      return null;
    }
  }
  
  return null;
};

interface PaginatedTableBodyProps {
  header: PaginatedTableColumn[];
  data: Record<string, Record<string, any>>;
  selected: string[];
  handleCheckboxClick: (key: string) => void;
  boxedCells?: boolean;
  boxedCellClass?: string;
  striped?: boolean;
  stripedColors?: string[] | ((theme: any) => string[]);
  actionsColumnId?: string;
  rowActions?: any[];
  rowActionsOutsideMenu?: boolean;
  addingRow?: boolean;
  editingRowId?: string | null;
  editRowValues?: Record<string, any>;
  editRowErrors?: Record<string, string>;
  onEditInputChange?: (colId: string, value: any) => void;
  onEditCancel?: () => void;
  onEditSave?: () => void;
  onEditRowClick?: (rowId: string, row: any) => void;
  addableColumns?: PaginatedTableColumn[];
  addRowAtBottom?: boolean;
  showEditOnTable?: boolean;
  wmsMode?: boolean;
  densityLevel?: 'comfortable' | 'standard' | 'compact';
}

// Create ColorParser class OUTSIDE the component
class ColorParser {
  private colorMap = new Map<string, string>();
  private initialized = false;
  
  constructor() {
    this.initializeColorMap();
  }
  
  private initializeColorMap() {
    if (this.initialized) return;
    
    // Add CSS colors
    Object.entries(cssColors).forEach(([name, hex]) => {
      this.addColorVariations(name, hex);
    });
    
    // Add colors from color-name-list
    // colorList.forEach(color => {
    //   this.addColorVariations(color.name, color.hex);
    // });
    
    // Add custom colors that might be missing
    const customColors = {
      'rose gold': '#E8B4B8',
      'nude': '#E3BC9A',
      'sand': '#C2B280',
      // 'golden': '#FFD700',
      'champagne': '#F7E7CE',
      'pearl': '#EAE0C8',
      'platinum': '#E5E4E2',
      'copper': '#B87333',
      'bronze': '#CD7F32',
      'gunmetal': '#2C3539',
      'charcoal': '#36454F',
      'steel blue': '#4682B4',
      'forest green': '#228B22',
      'burgundy': '#800020',
      'mauve': '#E0B0FF',
      'taupe': '#483C32',
      'ecru': '#C2B280',
      'ivory': '#FFFFF0',
      'cream': '#FFFDD0',
      'off white': '#FAF0E6',
      'warm gray': '#808069',
      'cool gray': '#8C92AC',
      'dusty rose': '#DCAE96',
      'sage': '#9CAF88',
      'sage green': '#9CAF88',
      'terracotta': '#E2725B',
      'clay': '#B66A50',
      'rust': '#B7410E',
      'ochre': '#CC7722',
      'umber': '#635147',
      'sienna': '#A0522D',
      'burnt orange': '#CC5500',
      'burnt sienna': '#E97451',
      'raw umber': '#826644',
      'sepia': '#704214',
      'mahogany': '#C04000',
      'walnut': '#773F1A',
      'chestnut': '#954535',
      'caramel': '#FFD59A',
      'honey': '#FFC30B',
      'amber': '#FFBF00',
      'mustard': '#FFDB58',
      'olive drab': '#6B8E23',
      'sage brush': '#9C9F84',
      'mint': '#3EB489',
      'seafoam': '#71EEB8',
      'teal blue': '#367588',
      'peacock': '#005F69',
      'navy blue': '#000080',
      'midnight blue': '#191970',
      'royal blue': '#4169E1',
      'cobalt': '#0047AB',
      'cerulean': '#007BA7',
      'azure': '#007FFF',
      'periwinkle': '#CCCCFF',
      'lavender gray': '#C4C3D0',
      'mauve taupe': '#915F6D',
      'dusty purple': '#825D78',
      'plum': '#8E4585',
      'eggplant': '#614051',
      'mulberry': '#C54B8C',
      'fuchsia': '#FF00FF',
      'hot pink': '#FF69B4',
      'bubblegum': '#FFC1CC',
      'cotton candy': '#FFBCD9',
      'blush': '#FFC0CB',
      'coral pink': '#F88379',
      'salmon pink': '#FF91A4',
      'peach': '#FFCBA4',
      'apricot': '#FBCEB1',
      'papaya': '#FFEFD5',
      'cantaloupe': '#FFA366',
      'tangerine': '#F28500',
      'sunset orange': '#FF8C69',
      'pumpkin': '#FF7518',
      'persimmon': '#EC5800',
      'cardinal': '#C41E3A',
      'crimson red': '#DC143C',
      'cherry': '#DE3163',
      'berry': '#8F021C',
      'wine': '#722F37',
      'bordeaux': '#6D071A',
      'maroon red': '#800000',
      // WMS/Industrial colors
      'safety orange': '#FF6600',
      'safety yellow': '#FFD300',
      'safety green': '#00A651',
      'safety red': '#C8102E',
      'warning amber': '#FFBF00',
      'caution yellow': '#FFD300',
      'danger red': '#C8102E',
      'go green': '#00A651',
      'stop red': '#C8102E',
      'alert orange': '#FF6600',
      'high vis yellow': '#FFD300',
      'high vis orange': '#FF6600',
      'high vis green': '#00A651',
      'reflective white': '#FFFFFF',
      'reflective silver': '#C0C0C0',
      'warehouse blue': '#1565C0',
      'forklift orange': '#FF6600',
      'pallet brown': '#8B4513',
      'steel gray': '#71797E',
      'concrete gray': '#A8A8A8',
      'machinery yellow': '#FFD300',
      'pipe blue': '#0066CC',
      'electrical orange': '#FF6600',
      'gas yellow': '#FFFF00',
      'water blue': '#0099CC',
    };
    
    Object.entries(customColors).forEach(([name, hex]) => {
      this.addColorVariations(name, hex);
    });
    
    this.initialized = true;
  }
  
  private addColorVariations(name: string, hex: string) {
    const variations = [
      name.toLowerCase(),
      name.toLowerCase().replace(/\s+/g, ''),
      name.toLowerCase().replace(/\s+/g, '-'),
      name.toLowerCase().replace(/\s+/g, '_'),
      name.toLowerCase().replace(/-/g, ''),
      name.toLowerCase().replace(/_/g, ''),
      name.toLowerCase().replace(/'/g, ''),
      name.toLowerCase().replace(/"/g, ''),
    ];
    
    variations.forEach(variation => {
      if (variation && !this.colorMap.has(variation)) {
        this.colorMap.set(variation, hex);
      }
    });
  }
  
  parse(colorValue: string): string {
    if (!colorValue || typeof colorValue !== 'string') return '#000000';
    
    const trimmed = colorValue.trim();
    if (!trimmed) return '#000000';
    
    // Try chroma-js first (handles hex, rgb, hsl, CSS colors, etc.)
    try {
      return chroma(trimmed).hex();
    } catch (error) {
      // Try our extended color map
      const normalized = trimmed.toLowerCase()
        .replace(/\s+/g, '')
        .replace(/-/g, '')
        .replace(/_/g, '')
        .replace(/'/g, '')
        .replace(/"/g, '');
      
      if (this.colorMap.has(normalized)) {
        return this.colorMap.get(normalized)!;
      }
      
      // Try fuzzy matching
      const fuzzyResult = this.fuzzyMatch(normalized);
      if (fuzzyResult) {
        return fuzzyResult;
      }
      
      // Last resort: browser parsing
      if (typeof window !== 'undefined' && document) {
        try {
          const div = document.createElement('div');
          div.style.color = trimmed;
          document.body.appendChild(div);
          
          const computedColor = window.getComputedStyle(div).color;
          document.body.removeChild(div);
          
          if (computedColor && computedColor !== 'rgba(0, 0, 0, 0)') {
            if (computedColor.startsWith('rgb')) {
              return this.rgbToHex(computedColor);
            }
            return computedColor;
          }
        } catch (browserError) {
          console.warn('Browser color parsing failed:', browserError);
        }
      }
    }
    
    console.warn('Color not found:', colorValue);
    return '#000000';
  }
  
  private fuzzyMatch(input: string): string | null {
    if (!input) return null;
    
    // Try partial matching
    for (const [name, hex] of this.colorMap.entries()) {
      if (name.includes(input) || input.includes(name)) {
        return hex;
      }
    }
    
    // Try matching without common modifiers
    const cleanInput = input.replace(/(light|dark|deep|pale|bright|medium|soft|hard|pure|true|real)/g, '');
    if (cleanInput !== input && cleanInput.length > 2) {
      for (const [name, hex] of this.colorMap.entries()) {
        if (name.includes(cleanInput) || cleanInput.includes(name)) {
          return hex;
        }
      }
    }
    
    return null;
  }
  
  private rgbToHex(rgb: string): string {
    const result = rgb.match(/\d+/g);
    if (result && result.length >= 3) {
      const [r, g, b] = result.map(Number);
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
    }
    return rgb;
  }
}

// Create a single instance outside the component
const colorParser = new ColorParser();

// Export the parse function for use throughout your app
export const parseColor = (colorValue: string): string => {
  return colorParser.parse(colorValue);
};

const PaginatedTableBody: React.FC<PaginatedTableBodyProps> = ({ 
  header, 
  data, 
  selected, 
  handleCheckboxClick, 
  boxedCells = false, 
  boxedCellClass = '', 
  striped = false, 
  stripedColors, 
  actionsColumnId, 
  rowActions, 
  rowActionsOutsideMenu = false, 
  addingRow = false, 
  editingRowId = null, 
  editRowValues = {}, 
  editRowErrors = {}, 
  onEditInputChange, 
  onEditCancel, 
  onEditSave, 
  onEditRowClick, 
  addableColumns, 
  showEditOnTable = false,
  wmsMode = false,
  densityLevel = 'standard'
}) => {
  console.log('PaginatedTableBody data:', data, 'header:', header);
  const theme = useMuiTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);

const BarcodeDisplay: React.FC<{ value: string; format?: string; width?: number; height?: number }> = ({
  value,
  format = 'CODE128',
  width = 2,
  height = 50
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (canvasRef.current && value) {
      try {
        JsBarcode(canvasRef.current, value, {
          format: format,
          width: width,
          height: height,
          displayValue: true,
          fontSize: 12,
          textMargin: 2,
          margin: 2
        });
      } catch (error) {
        console.error('Barcode generation error:', error);
      }
    }
  }, [value, format, width, height]);

  if (!value) return <Typography variant="body2">No data</Typography>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      dispatch(showAlert({ message: 'Barcode copied to clipboard!', type: 'success' }));
    } catch (error) {
      dispatch(showAlert({ message: 'Failed to copy barcode.', type: 'error' }));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '200px',
        cursor: 'pointer',
        '&:hover': {
          opacity: 0.8,
        },
      }}
      onClick={handleCopy}
      title="Click to copy"
    >
      <canvas ref={canvasRef} style={{ maxWidth: '100%' }} />
    </Box>
  );
};


  const getWMSRowHeight = () => {
    switch (densityLevel) {
      case 'compact': return 32;
      case 'comfortable': return 48;
      default: return 40;
    }
  };

  const getWMSColors = () => {
    if (!wmsMode) return null;
    
    return {
      primary: theme.palette.mode === 'dark' ? '#1976d2' : '#1565c0',
      secondary: theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5',
      selected: theme.palette.mode === 'dark' ? '#1976d220' : '#1565c015',
      hover: theme.palette.mode === 'dark' ? '#42424220' : '#f5f5f5',
      border: theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0',
      error: '#d32f2f',
      warning: '#ed6c02',
      success: '#2e7d32',
    };
  };

  const wmsColors = getWMSColors();

  const renderCellContent = (value: any, head: PaginatedTableColumn, rowId: string, row: Record<string, any>) => {
    switch (head.type) {
      case PaginatedTableColumnType.TEXT:
        return value;
      case PaginatedTableColumnType.NUMBER:
        return value;
      case PaginatedTableColumnType.DATE:
        if (!value) return '';
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return value;
          return formatDate(value);
        } catch {
          return value;
        }
      case PaginatedTableColumnType.DATETIME:
        if (!value) return '';
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return value;
          
          // Enhanced datetime display with structured layout
          const dateStr = date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: '2-digit', 
            year: 'numeric' 
          });
          const timeStr = date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            second: '2-digit',
            hour12: false 
          });
          
          return (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {dateStr}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {timeStr}
              </Typography>
            </Box>
          );
        } catch {
          return value;
        }
      case PaginatedTableColumnType.CUSTOM:
        // For custom types without a render function, try auto-detection
        const customAutoFormattedDate = detectAndFormatDate(value);
        if (customAutoFormattedDate) {
          return customAutoFormattedDate;
        }
        return value;
      case PaginatedTableColumnType.COLORED_CHIP: {
        const chipStyle = head.chipColors ? head.chipColors[value] : undefined;
        return (
          <Tooltip title={value}>
            <Chip
              label={value}
              style={{
                cursor: "pointer",
                backgroundColor: chipStyle ? chipStyle.backgroundColor : 'white',
                color: chipStyle ? chipStyle.color : 'black',
                minHeight: densityLevel === 'compact' ? '20px' : '24px',
                fontSize: densityLevel === 'compact' ? '0.75rem' : '0.8125rem',
              }}
              sx={{
                '& .MuiChip-label': {
                  display: 'block',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflow: 'visible',
                  textOverflow: 'unset',
                  lineHeight: '1.2',
                  padding: densityLevel === 'compact' ? '1px 6px' : '2px 8px',
                  maxWidth: '120px',
                },
                display: 'inline-flex',
                maxWidth: '100%',
                height: 'auto',
                minHeight: densityLevel === 'compact' ? '20px' : '24px',
                borderRadius: wmsMode ? '4px' : '16px',
              }}
            />
          </Tooltip>
        );
      }
      case PaginatedTableColumnType.COLOR: {
        // FIXED: Call parseColor with the actual value, not the function reference
        const colorValue = parseColor(value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: densityLevel === 'compact' ? 12 : 16,
                height: densityLevel === 'compact' ? 12 : 16,
                backgroundColor: colorValue, // Use the parsed color value
                border: '1px solid',
                borderColor: wmsColors?.border || theme.palette.divider,
                borderRadius: wmsMode ? '2px' : '50%',
                flexShrink: 0,
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                fontSize: densityLevel === 'compact' ? '0.75rem' : '0.875rem'
              }}
            >
              {value}
            </Typography>
          </Box>
        );
      }
      case PaginatedTableColumnType.BARCODE: {
        const barcodeConfig = (head as any).barcodeConfig || {};
        return (
          <BarcodeDisplay 
            value={value} 
            format={barcodeConfig.format || 'CODE128'}
            width={barcodeConfig.width || (densityLevel === 'compact' ? 1 : 2)}
            height={barcodeConfig.height || (densityLevel === 'compact' ? 30 : 40)}
          />
        );
      }
      case PaginatedTableColumnType.LINK:
        return <Link href={typeof (head as any).href === 'function' ? (head as any).href(row) : '#'}>{value}</Link>;
      case PaginatedTableColumnType.CLICKABLE:
        return (
          <Link
            type="button"
            onClick={() => (head as any).onClick(rowId, row)}
            sx={{
              color: theme.palette.text.primary,
              cursor: 'pointer',
              textDecoration: 'underline',
              transition: 'color 0.2s',
              '&:hover': {
                color: wmsColors?.primary || theme.palette.primary.main,
              },
            }}
          >
            {value}
          </Link>
        );
      case PaginatedTableColumnType.CURRENCY:
        return formatCurrencyNumber(value);
      case PaginatedTableColumnType.PHONE_NUMBER:
        return formatIntPhoneNumber(value);
      case PaginatedTableColumnType.ACTION: {
        return null;
      }
      case PaginatedTableColumnType.PROGRESS: {
        const expected = row[(head as any).expectedField];
        const actual = row[(head as any).actualField];
        const dueDate = (head as any).dueDateField ? row[(head as any).dueDateField] : undefined;
        const status = row.status;
        let color: 'success' | 'error' | 'warning' = 'success';
        let icon: React.ReactNode = <CheckCircleIcon color="success" />;
        let helperText = '';
        const isFinished = status === 'Completed' || status === 'cancelled';
        if (!isFinished && dueDate && dayjs().isAfter(dayjs(dueDate))) {
          color = 'warning';
          icon = <WarningIcon color="warning" />;
          helperText = 'Overdue';
        } else if (actual > expected) {
          color = 'error';
          icon = <ErrorIcon color="error" />;
          helperText = 'Over target';
        }
        const percent = expected > 0 ? Math.min((actual / expected) * 100, 100) : 0;
        return (
          <Box sx={{ minWidth: wmsMode ? 120 : 150 }}>
            <Tooltip title={`${actual} / ${expected} hours`} arrow>
              <Stack direction="row" alignItems="center" spacing={1}>
                {icon}
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  color={color}
                  sx={{
                    height: densityLevel === 'compact' ? 6 : 8,
                    borderRadius: wmsMode ? 2 : 6,
                    flex: 1,
                    backgroundColor: wmsColors?.secondary || '#e0e0e0'
                  }}
                />
              </Stack>
            </Tooltip>
            <Box sx={{ pt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography 
                sx={{ 
                  color: theme.palette[color].main,
                  fontSize: densityLevel === 'compact' ? '0.7rem' : '0.75rem'
                }}
              >
                {helperText}
              </Typography>
              <Typography 
                color="text.secondary" 
                fontSize={densityLevel === 'compact' ? 10 : 12}
              >
                {dueDate ? `Due: ${formatDate(dueDate)}` : ''}
              </Typography>
            </Box>
          </Box>
        );
      }
      default:
        // Auto-detect and format ISO date strings for any column type
        const autoFormattedDate = detectAndFormatDate(value);
        if (autoFormattedDate) {
          return autoFormattedDate;
        }
        return value;
    }
  };

  // Helper to render the add row
  const renderAddRow = () => (
    <TableRow style={{ backgroundColor: wmsColors?.secondary || theme.palette.action.selected }}>
      <TableCell padding="checkbox" className={boxedCells ? boxedCellClass : undefined} />
      {header.map((column) => {
        if (!addableColumns?.some(c => c.id === column.id)) {
          return <TableCell key={column.id} className={boxedCells ? boxedCellClass : undefined} />;
        }
        return (
          <TableCell key={column.id} className={boxedCells ? boxedCellClass : undefined}>
            <TextField
              value={editRowValues[column.id] || ''}
              onChange={e => onEditInputChange && onEditInputChange(column.id, e.target.value)}
              size={densityLevel === 'compact' ? 'small' : 'small'}
              error={!!editRowErrors[column.id]}
              helperText={editRowErrors[column.id]}
              placeholder={column.label}
              autoFocus={header.findIndex(h => h.id === column.id) === 0}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  onEditSave && onEditSave();
                } else if (e.key === 'Escape') {
                  onEditCancel && onEditCancel();
                }
              }}
            />
          </TableCell>
        );
      })}
      <TableCell className={boxedCells ? boxedCellClass : undefined}>
        <Stack direction="row" spacing={1}>
          <Button color="primary" size="small" variant="contained" onClick={onEditSave}>Save</Button>
          <Button color="inherit" size="small" variant="outlined" onClick={onEditCancel}>Cancel</Button>
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <TableBody>
      {addingRow && renderAddRow()}
      {Object.entries(data).map(([key, row], idx) => {
        // Inline edit row
        if (editingRowId === key) {
          return (
            <TableRow key={key} style={{ backgroundColor: wmsColors?.secondary || theme.palette.action.selected }}>
              <TableCell padding="checkbox" className={boxedCells ? boxedCellClass : undefined} />
              {header.map((column) => {
                if (column.editable === false) {
                  return <TableCell key={column.id} className={boxedCells ? boxedCellClass : undefined} />;
                }
                return (
                  <TableCell key={column.id} className={boxedCells ? boxedCellClass : undefined}>
                    <TextField
                      value={editRowValues[column.id] || ''}
                      onChange={e => onEditInputChange && onEditInputChange(column.id, e.target.value)}
                      size="small"
                      error={!!editRowErrors[column.id]}
                      helperText={editRowErrors[column.id]}
                      placeholder={column.label}
                      autoFocus={header.findIndex(h => h.id === column.id) === 0}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          onEditSave && onEditSave();
                        } else if (e.key === 'Escape') {
                          onEditCancel && onEditCancel();
                        }
                      }}
                    />
                  </TableCell>
                );
              })}
              <TableCell className={boxedCells ? boxedCellClass : undefined}>
                <Stack direction="row" spacing={1}>
                  <Button color="primary" size="small" variant="contained" onClick={onEditSave}>Save</Button>
                  <Button color="inherit" size="small" variant="outlined" onClick={onEditCancel}>Cancel</Button>
                </Stack>
              </TableCell>
            </TableRow>
          );
        }
        
        // Determine row background and styling
        const isSelected = selected.includes(key);
        let rowBg = theme.palette.background.paper;
        
        // WMS-optimized selection styling (no movement, just background)
        if (isSelected) {
          rowBg = wmsColors?.selected || theme.palette.action.selected;
        } else if (striped && stripedColors) {
          let colors = typeof stripedColors === 'function' ? stripedColors(theme) : stripedColors;
          rowBg = colors[idx % 2] || theme.palette.background.paper;
        } else if (wmsMode && striped) {
          // WMS-specific subtle alternating colors
          rowBg = idx % 2 === 0 
            ? theme.palette.background.paper 
            : (theme.palette.mode === 'dark' ? '#ffffff05' : '#00000005');
        }
        
        return (
          <TableRow 
            key={key} 
            tabIndex={-1} 
            hover={!isSelected}
            style={{
              backgroundColor: rowBg,
              transition: wmsMode ? 'background-color 0.15s ease' : 'all 0.2s ease',
              height: getWMSRowHeight(),
              border: wmsMode && boxedCells ? `1px solid ${wmsColors?.border}` : 'none',
            }}
            sx={{
              '&:hover': !isSelected ? {
                backgroundColor: wmsColors?.hover || theme.palette.action.hover,
              } : {},
              // No transform/movement for WMS mode
            }}
          >
            <TableCell 
              padding="checkbox" 
              className={boxedCells ? boxedCellClass : undefined} 
              sx={{ 
                padding: theme.spacing(0.5, 1),
                backgroundColor: 'transparent'
              }}
            >
              <Checkbox
                checked={isSelected}
                inputProps={{
                  'aria-labelledby': `Paginated-table-checkbox-${key}`,
                }}
                onClick={() => handleCheckboxClick(key)}
                size={densityLevel === 'compact' ? 'small' : 'small'}
                color="primary"
                sx={{
                  // No scaling animation for WMS
                  '&.Mui-checked': {
                    color: wmsColors?.primary || theme.palette.primary.main,
                  }
                }}
              />
            </TableCell>
            {header.map((column) => {
              if (actionsColumnId && column.id === actionsColumnId) {
                const allRowActions = (column as any).actions || [];
                const visibleRowActions = allRowActions.filter(action => !action.visible || action.visible(row));
                return (
                  <TableCell
                    key={column.id}
                    align={column.numeric ? 'right' : 'left'}
                    style={{ 
                      padding: theme.spacing(0.5, 1), 
                      color: wmsColors?.primary || theme.palette.primary.main,
                      backgroundColor: 'transparent',
                    }}
                    className={boxedCells ? boxedCellClass : undefined}
                  >
                    {showEditOnTable && onEditRowClick && (
                      <Tooltip title="Edit">
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => onEditRowClick(key, row)} 
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {rowActionsOutsideMenu || visibleRowActions.length === 1 ? (
                      visibleRowActions.map((action, idx) => (
                        <Tooltip title={action.title || action.label || ''} key={action.key || idx}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => action.onClick(key, row)}
                              disabled={action.disabled && action.disabled(row)}
                              color={action.color || 'primary'}
                            >
                              {action.icon}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ))
                    ) : (
                      visibleRowActions.length > 1 && (
                        <>
                          <IconButton 
                            size="small" 
                            onClick={e => { setMenuAnchor(e.currentTarget); setMenuRowId(key); }} 
                            color="primary"
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            anchorEl={menuAnchor}
                            open={menuRowId === key && Boolean(menuAnchor)}
                            onClose={() => { setMenuAnchor(null); setMenuRowId(null); }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            {visibleRowActions.map((action, idx) => (
                              <MenuItem key={action.key || idx} onClick={() => { action.onClick(key, row); setMenuAnchor(null); setMenuRowId(null); }}>
                                {action.icon}&nbsp;{action.label}
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )
                    )}
                  </TableCell>
                );
              }
              return (
                <TableCell
                  key={column.id}
                  align={column.numeric ? 'right' : 'left'}
                  style={{ 
                    padding: theme.spacing(0.5, 1), 
                    color: theme.palette.text.primary, 
                    fontSize: densityLevel === 'compact' ? '0.75rem' : theme.typography.body2.fontSize,
                    backgroundColor: 'transparent',
                  }}
                  className={boxedCells ? boxedCellClass : undefined}
                >
                  {column.type === PaginatedTableColumnType.CUSTOM
                    ? column.render?.(row[column.id], row, key) ?? renderCellContent(row[column.id], column, key, row)
                    : renderCellContent(row[column.id], column, key, row)}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export default PaginatedTableBody;